import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import {
  downloadAmortizationSchedule,
  downloadAmortizationScheduleExcel,
  downloadFile,
  getAmortizationSchedule,
  saveAdditionalPrincipalReduction,
  saveAmortizationScheduleSettings,
} from 'services/apis'
import { formatDate, formatDateYMD, getPrice2decimal, removeComma, roundToCents } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

const headers = ['Year', 'No', 'Date', 'P&I', 'Interest', 'Princ. Red.', 'Addi P R', 'Balance', 'Rate', 'Adjusts']

export default function AmortizationScheduleContent() {
  const [action, setAction] = useState('')
  const [product, setProduct] = useState<any>({})
  const [data, setData] = useState<any>([])
  const [inputs, setInputs] = useState<any>({
    period: {
      title: 'Additional Principal Reduction',
      inputType: 'select',
      hasDefaultOption: false,
      options: {
        0: 'None',
        1: 'Every Month',
        2: 'Every 2 Months',
        3: 'Every 3 Months',
        4: 'Every 4 Months',
        6: 'Every 6 Months',
        12: 'Every 12 Months',
      },
      value: 0,
    },
    amount: {
      title: 'Amount',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      value: '',
    },
  })
  const [tableData, setTableData] = useState<string[][]>([])
  const [addiPrincReductions, setAddiPrincReductions] = useState<Array<number | null>>([])

  useEffect(() => {
    setLoanNumber()
    init()
  }, [])

  useEffect(() => {
    let firstPaymentDate = formatDate(data.firstPaymentDate)
    if (data.rate == -1 || data.rate == 0 || !firstPaymentDate.length) {
      setTableData([])
      return
    }

    const rows: string[][] = []
    let balance = removeComma(data.loanAmount)
    const additionalPrincipalReductionPeriod = removeComma(inputs.period.value)
    const additionalPrincipalReductionAmount = removeComma(inputs.amount.value)
    const rate = removeComma(data.rate) / 100
    let adjust = 'First Payment'
    let mortgagePayment = removeComma(data.mortgagePayment)
    let totalPayment = 0
    let totalInterest = 0
    let totalScheduledPrincipal = 0
    let totalAdditionalPrincipal = 0

    for (let i = 1; i <= data.loanTerm && balance > 0; i += 1) {
      const year = Math.floor((i - 1) / 12) + 1
      // A month of interest is a balance times a rate over twelve, which lands on an exact half
      // cent constantly - $27,000 at 12.49% is exactly $281.025. `Number(x.toFixed(2))` rounds the
      // binary double, which for such a decimal sits just under the midpoint, so it billed $281.02
      // against the $281.03 the loan is actually billed, and Principal (the plug for whatever
      // interest leaves behind) collected the stray cent. `roundToCents` reads the decimal the
      // arithmetic meant. The figures below are sums, differences and clamps of whole cents, which
      // have no third decimal to decide, so they stay as they are.
      const interest = roundToCents((balance * rate) / 12)
      const scheduledPrincipal = Number(Math.min(Math.max(mortgagePayment - interest, 0), balance).toFixed(2))
      const balanceAfterScheduledPrincipal = balance - scheduledPrincipal
      const requestedAdditionalPrincipal =
        additionalPrincipalReductionPeriod > 0 && i % additionalPrincipalReductionPeriod === 0
          ? additionalPrincipalReductionAmount
          : 0
      const additionalPrincipal =
        addiPrincReductions[i - 1] ??
        Number(Math.min(Math.max(requestedAdditionalPrincipal, 0), balanceAfterScheduledPrincipal).toFixed(2))
      const endingBalance = Number(Math.max(balanceAfterScheduledPrincipal - additionalPrincipal, 0).toFixed(2))
      const payment = Number((interest + scheduledPrincipal + additionalPrincipal).toFixed(2))

      totalPayment += payment
      totalInterest += interest
      totalScheduledPrincipal += scheduledPrincipal
      totalAdditionalPrincipal += additionalPrincipal

      rows.push([
        `${year}`,
        `${i}`,
        firstPaymentDate,
        `$${getPrice2decimal(payment, false, true)}`,
        `$${getPrice2decimal(interest, false, true)}`,
        `$${getPrice2decimal(scheduledPrincipal, false, true)}`,
        `$${getPrice2decimal(additionalPrincipal, false, true)}`,
        `$${getPrice2decimal(endingBalance, false, true)}`,
        `${removeComma(data.rate).toFixed(3)}`,
        adjust,
      ])

      balance = endingBalance
      firstPaymentDate = DateTime.fromFormat(firstPaymentDate, 'MM/dd/yyyy').plus({ months: 1 }).toFormat('MM/dd/yyyy')
      if (i === 1) adjust = ''
      if (i == data.ioMonths) {
        mortgagePayment = removeComma(data.interestOnlyExpired)
        adjust = 'Interest Only Expired'
      }
      if (i == data.ioMonths + 1) {
        adjust = ''
      }
    }

    rows.push([
      '',
      '',
      'Totals',
      `$${getPrice2decimal(totalPayment.toFixed(2), false, true)}`,
      `$${getPrice2decimal(totalInterest.toFixed(2), false, true)}`,
      `$${getPrice2decimal(totalScheduledPrincipal.toFixed(2), false, true)}`,
      `$${getPrice2decimal(totalAdditionalPrincipal.toFixed(2), false, true)}`,
      '',
      '',
      '',
    ])
    setTableData(rows)
  }, [data, inputs, addiPrincReductions])

  const onChangeAdditionalPrincipalReduction = async (value: any, index: number) => {
    value = removeComma(value)
    const nextAddiPrincReductions = cloneDeep(addiPrincReductions)
    nextAddiPrincReductions[index] = value
    setAddiPrincReductions(nextAddiPrincReductions)

    setAction('loading')
    try {
      await saveAdditionalPrincipalReduction(index, value)
    } finally {
      setAction('')
    }
  }

  const init = () => {
    setAction('loading')
    getAmortizationSchedule()
      .then((res) => {
        if (res.success) {
          setData(res.data)
          setProduct(res.product)
          setAddiPrincReductions(res.data.addiPrincReductions)
          setInputs((currentInputs: any) => {
            const nextInputs = cloneDeep(currentInputs)
            nextInputs.period.value = res.data.additionalPrincipalReductionPeriod || 0
            nextInputs.amount.value = removeComma(res.data.additionalPrincipalReductionAmount) || ''
            return nextInputs
          })
        }
      })
      .finally(() => setAction(''))
  }

  const onChange = (key: string, e: any) => {
    let temp = cloneDeep(inputs)
    temp[key].value = e
    setInputs(temp)
  }

  const onBlur = () => {
    setAction('loading')
    saveAmortizationScheduleSettings(inputs.period.value, inputs.amount.value).finally(() => setAction(''))
  }

  const onExportExcel = async () => {
    const loanNumber = setLoanNumber()
    setAction('loading')

    try {
      const excelData = await downloadAmortizationScheduleExcel(tableData, {
        period: inputs.period.options[inputs.period.value],
        amount: inputs.amount.value,
      })
      downloadFile(`Amortization Schedule ${loanNumber}(${formatDateYMD()}).xlsx`, excelData)
    } finally {
      setAction('')
    }
  }

  const onDownloadPdf = async () => {
    const loanNumber = setLoanNumber()
    setAction('loading')

    const pdfData = await downloadAmortizationSchedule(tableData)
    downloadFile(`Amortization Schedule ${loanNumber}(${formatDateYMD()}).pdf`, pdfData)
    setAction('')
  }

  const renderTable = useMemo(() => {
    return (
      <div className="relative overflow-auto mb-6 shadow-md rounded">
        <table className={`w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6`}>
          <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              {headers.map((item, index) => (
                <th scope="col" className={`px-4 py-3 ${index === 6 ? 'w-[150px]' : ''}`} key={index}>
                  {item}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-[14.5px] text-gray-900">
            {tableData.map((row, index) => (
              <tr
                key={index}
                className={`${index === tableData.length - 1 ? 'font-bold' : ''} ${index % 2 ? 'bg-gray-50' : ''}`}
              >
                {row &&
                  row.map((item, rIndex) => {
                    if ((item as any) === true) item = 'Y'
                    if ((item as any) === false) item = 'N'

                    return (
                      <td
                        key={rIndex}
                        className={`px-4 py-2 whitespace-nowrap ${
                          rIndex == 0 && 'font-medium text-gray-900 dark:text-white'
                        }`}
                      >
                        {rIndex === 6 ? (
                          <PlainInput
                            value={item || ''}
                            content={item || ''}
                            onChange={(value) => onChangeAdditionalPrincipalReduction(value, index)}
                          />
                        ) : (
                          <span>{item}</span>
                        )}
                      </td>
                    )
                  })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }, [tableData])

  return (
    <>
      <LayoutLoading show={action.length > 0} />
      <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
        <span>Amortization Schedule</span>
      </h2>
      <div className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-5">
          {Object.keys(product).map((key, index) => {
            const input = product[key]
            return (
              <div key={index} className={`md:col-span-${input.span || 1}`}>
                <RenderInput
                  input={{
                    inputType: 'text',
                    readOnly: true,
                    value: input.value,
                    title: input.title,
                    prefix: input.prefix,
                  }}
                  Key={key}
                  onChange={onChange}
                />
              </div>
            )
          })}
        </div>
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            {Object.keys(inputs).map((key, index) => {
              let input = inputs[key]
              if (key === 'closing') input.value = data.closingDate
              return (
                <div key={index} className="md:col-span-1">
                  <RenderInput input={input} Key={key} onChange={onChange} onBlur={onBlur} />
                </div>
              )
            })}
          </div>
          <div className="flex gap-2 items-center">
            <button className="hover:underline text-[14px] text-shade-blue p-2" onClick={onExportExcel}>
              <div className="flex items-center">
                <ArrowDownTrayIcon className="w-4 h-4 mr-1" /> Export to Excel
              </div>
            </button>
            <button className="hover:underline text-[14px] text-shade-blue p-2" onClick={onDownloadPdf}>
              <div className="flex items-center">
                <ArrowDownTrayIcon className="w-4 h-4 mr-1" /> Download to PDF
              </div>
            </button>
          </div>
        </div>

        {renderTable}
      </div>
    </>
  )
}
